/**
 * Copyright 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo } from "react";
import {
  Granularity as APIGranularity,
  useApplicationsCostPerCategoryQuery,
} from "~/generated/graphql";
import {
  mapGranularity,
  ChartType,
  CostAnalysisFields,
  Granularity,
  TableData,
  generateApplicationGroupData,
} from "~/components";
import { useQueryState } from "~/hooks";
import { isNotEmpty, isNotNil } from "~/tools";
import { getCategoriesList } from "~/views/applications/ApplicationCostTabs/hooks/utils";
import { generateDataWithCorrections } from "./helpers";

type Props = {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  showOtherCategoriesCost: boolean;
  handleOtherCostsSwitch: (enable: boolean) => void;
};

type Pagination = {
  page: number;
  limit: number;
};
export function useApplicationsCostPerCategory({
  startDate,
  endDate,
  granularity,
  showOtherCategoriesCost,
  handleOtherCostsSwitch,
}: Props) {
  const { state } = useQueryState<Pagination>();

  const { data, loading, error } = useApplicationsCostPerCategoryQuery({
    variables: {
      limit: 10,
      page: state.page,
      startDate,
      endDate,
      granularity: mapGranularity(granularity),
      tableGranularity: APIGranularity.Monthly,
    },
  });

  useEffect(() => {
    if (!loading) {
      const pages = Number(data?.customerCategoriesCost.pages ?? 0);
      handleOtherCostsSwitch(pages > 1);
    }
  }, [data, loading, handleOtherCostsSwitch]);

  const costPerCategoryTableTimePoints = useMemo(
    () => (data?.customerCategoriesTableCost.timePoints ?? []).filter(isNotNil),
    [data]
  );

  const costPerCategoryTableData: TableData[] = useMemo(
    () =>
      generateApplicationGroupData(
        costPerCategoryTableTimePoints ?? [],
        showOtherCategoriesCost,
        ChartType.COST_PER_CATEGORY
      ),
    [costPerCategoryTableTimePoints, showOtherCategoriesCost]
  );

  const costPerCategoryChartData = useMemo(
    () =>
      generateDataWithCorrections(
        (data?.customerCategoriesCost.timePoints ?? []).filter(isNotNil),
        (data?.customerCorrectionCost.timePoints ?? []).filter(isNotNil)
      ),
    [data]
  );

  const isCorrections = useMemo(
    () =>
      isNotEmpty(
        (data?.customerCorrectionCost.timePoints ?? []).filter(isNotNil)
      ),
    [data]
  );

  const costPerCategoryTotal = Number(data?.customerCategoriesTableCost ?? "0");

  const categories = getCategoriesList(costPerCategoryTableData).filter(
    (category) => category !== CostAnalysisFields.TOTAL_COST
  );

  return {
    costPerCategoryChartData,
    costPerCategoryTableData,
    costPerCategoryTotal,
    loading,
    error,
    count: data?.customerCategoriesTableCost.count,
    categories,
    isCorrections,
  };
}
